import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getRegistration } from "@/lib/api";
import BoardingPass from "@/components/BoardingPass";
import { ArrowLeft } from "lucide-react";

/**
 * Public read-only boarding pass view. Anyone with the link
 * (`/pass/{registration_id}`) can see the pass — no personal contact info
 * beyond name is shown. Reached from the WhatsApp share deep-link.
 */
export default function PublicPass() {
  const { id } = useParams();
  const [registration, setRegistration] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const doc = await getRegistration(id);
        if (!cancelled) {
          setRegistration(doc);
          setStatus("ok");
        }
      } catch {
        if (!cancelled) setStatus("notfound");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="min-h-[calc(100vh-140px)] bg-offwhite">
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link
          to="/event"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest text-sm mb-8"
        >
          <ArrowLeft size={14} /> Register for Chapter III
        </Link>

        {status === "loading" && (
          <div className="text-forest/60 text-sm">Loading boarding pass&hellip;</div>
        )}

        {status === "notfound" && (
          <div className="border border-forest/15 rounded-2xl p-8 bg-white text-center space-y-3">
            <div className="font-display text-2xl text-forest">Pass not found</div>
            <p className="text-sm text-forest/60">
              This boarding pass link is invalid or has expired. Registrations open on the event page.
            </p>
            <Link to="/event" className="btn-primary text-sm inline-flex mt-2">
              Register for Chapter III
            </Link>
          </div>
        )}

        {status === "ok" && registration && (
          <div className="space-y-6">
            <div>
              <div className="eyebrow text-teal mb-2">Boarding pass</div>
              <h1 className="font-display text-forest text-3xl md:text-4xl leading-tight">
                {registration.payload.name.split(" ")[0]} is on the guest list.
              </h1>
              <p className="mt-3 text-sm text-forest/70">
                Chapter III &middot; Sept 12&ndash;13, 2026 &middot; Pali Hill City ridge
              </p>
            </div>
            <BoardingPass registration={registration} publicView />
            <div className="pt-4">
              <Link to="/event" className="btn-primary text-sm inline-flex">
                Register your own pass
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
